import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AppRoutes from './routes/AppRoutes';
import { UIProvider } from './shared/UI/Uicontext';
import { useAuthKey } from './store/api/authKey';
import { AppDispatch } from './store';
import { loadSession, clearSession } from './store/slices/sessionSlice';
import {
  decodeSessionFromStorage,
  refreshTokenExpiry,
  isSessionExpired,
} from './shared/services/userSessionService';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function App() {
  useAuthKey();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  useEffect(() => {
    // Case 1 & 2: on mount, check if the session is expired.
    // If expired, clear everything and redirect to login.
    if (isSessionExpired()) {
      // If Angular JUST bridged the session, give it a moment — check again
      const bridgeSync = localStorage.getItem('AUTH_BRIDGE_SYNC');
      if (bridgeSync === 'true') {
        // Angular primed session — recheck after a tick
        localStorage.removeItem('AUTH_BRIDGE_SYNC');
        const session = decodeSessionFromStorage();
        if (session) {
          dispatch(loadSession(session));
          return;
        }
      }

      localStorage.removeItem('token');
      localStorage.removeItem('username');
      localStorage.removeItem('ModList');
      localStorage.removeItem('MenuList');
      localStorage.removeItem('ReactMenus');
      localStorage.removeItem('ReactMenuChildren');
      dispatch(clearSession());
      navigate('/', { replace: true });
      return;
    }

    const session = decodeSessionFromStorage();
    if (session) dispatch(loadSession(session));
  }, [dispatch, navigate]);
  // Case 4: user is actively using the app — every 1 hour, bump the exp
  // field inside the stored token by +3600s and re-store it. No API call.
  useEffect(() => {
    const interval = setInterval(
      () => {
        if (!isSessionExpired()) {
          refreshTokenExpiry();
        }
      },
      60 * 60 * 1000
    ); // 1 hour

    return () => clearInterval(interval);
  }, []);

  return (
    <UIProvider>
      <style>{`
        .Toastify__toast--error { background: #c0392b !important; color: #fff !important; }
        .Toastify__toast--success { background: #28a745 !important; color: #fff !important; }
        .Toastify__toast--warning { background: #ff9800 !important; color: #fff !important; }
        .Toastify__toast--info { background: #2196f3 !important; color: #fff !important; }
        .Toastify__close-button { color: #fff !important; opacity: 1 !important; }
      `}</style>
      <AppRoutes />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        pauseOnFocusLoss={false}
        draggable
        theme="colored"
        limit={1}
      />
    </UIProvider>
  );
}

export default App;
